import {
  Bot as BotIcon,
  Check,
  Copy,
  LayoutGrid,
  Plus,
  RotateCw,
  Search,
  Table as TableIcon,
  Trash2,
  UserRound,
} from 'lucide-react';
import { useMemo, useState } from 'react';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { SortableTableHead } from '@/components/ui/sortable-table-head';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ViewModeToggle, type ViewModeOption } from '@/components/ui/view-mode-toggle';
import type { ApiError } from '@/core/api';
import { MemberSelect, useRoles } from '@/features/members';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { cn } from '@/lib/utils';
import type { Bot } from '../api';
import { useBots, useCreateBot, useRevokeBot, useRotateBotKey } from '../queries';
import { useKeysUi, type KeysViewMode } from '../store/keysViewPrefs';

function formatDateTime(value: string | null): string {
  if (!value) return '—';
  return new Date(value).toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
}

/** Shows a freshly-minted raw key once, with copy-to-clipboard. */
function RevealKeyDialog({
  apiKey,
  onClose,
}: {
  apiKey: string | null;
  onClose: () => void;
}) {
  const [copied, setCopied] = useState(false);

  async function copy() {
    try {
      await navigator.clipboard.writeText(apiKey ?? '');
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard may be unavailable (insecure context) — the key is still visible to select.
    }
  }

  return (
    <Dialog open={apiKey != null} onOpenChange={(open) => (open ? null : onClose())}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Copy your API key</DialogTitle>
          <DialogDescription>
            This is the <strong>only time</strong> the key is shown. Store it somewhere safe —
            you can&apos;t retrieve it again, only rotate it.
          </DialogDescription>
        </DialogHeader>
        <div className="flex items-center gap-2">
          <Input readOnly value={apiKey ?? ''} className="font-mono text-xs" />
          <Button type="button" variant="outline" size="icon" onClick={copy} aria-label="Copy key">
            {copied ? <Check className="size-4" /> : <Copy className="size-4" />}
          </Button>
        </div>
        <DialogFooter>
          <Button type="button" onClick={onClose}>
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

type CreateMode = 'bot' | 'user';

/** Create dialog: a synthetic service **bot** (name + roles) or a **personal
 * access token** acting as an existing user (name + member picker). */
function CreateBotDialog({
  open,
  onOpenChange,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (apiKey: string) => void;
}) {
  const rolesQuery = useRoles();
  const create = useCreateBot();
  const [mode, setMode] = useState<CreateMode>('bot');
  const [name, setName] = useState('');
  const [roleIds, setRoleIds] = useState<number[]>([]);
  const [userId, setUserId] = useState('');
  const [error, setError] = useState<string | null>(null);

  function reset() {
    setMode('bot');
    setName('');
    setRoleIds([]);
    setUserId('');
    setError(null);
  }

  async function submit() {
    setError(null);
    if (!name.trim()) {
      setError('Give the key a name.');
      return;
    }
    if (mode === 'user' && !userId) {
      setError('Pick the user this token will act as.');
      return;
    }
    try {
      const bot = await create.mutateAsync(
        mode === 'user'
          ? { name: name.trim(), user_id: Number(userId) }
          : { name: name.trim(), role_ids: roleIds },
      );
      reset();
      onOpenChange(false);
      onCreated(bot.api_key);
    } catch (e) {
      setError((e as ApiError)?.message ?? 'Could not create the key.');
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) reset();
        onOpenChange(o);
      }}
    >
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>New API key</DialogTitle>
          <DialogDescription>
            A <strong>bot</strong> is a standalone service account with the roles you grant it.
            A <strong>personal token</strong> acts as an existing person, with their own roles
            and access.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          <div className="grid grid-cols-2 gap-2" role="group" aria-label="Key type">
            {(['bot', 'user'] as const).map((m) => (
              <button
                key={m}
                type="button"
                aria-pressed={mode === m}
                onClick={() => setMode(m)}
                className={cn(
                  'cursor-pointer rounded-md border px-3 py-2 text-sm transition-colors',
                  mode === m
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'border-input hover:bg-accent',
                )}
              >
                {m === 'bot' ? 'Service bot' : 'Existing user'}
              </button>
            ))}
          </div>

          <div className="space-y-2">
            <Label htmlFor="bot-name">Name</Label>
            <Input
              id="bot-name"
              placeholder={mode === 'user' ? "e.g. Ada's laptop" : 'e.g. MCP Server, Zapier'}
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          {mode === 'bot' ? (
            <div className="space-y-2">
              <Label>Roles</Label>
              <div className="flex flex-wrap gap-2" role="group" aria-label="Roles">
                {rolesQuery.isPending ? (
                  <span className="text-muted-foreground text-sm">Loading roles…</span>
                ) : (
                  rolesQuery.data
                    ?.filter((r) => r.title !== 'Guest')
                    .map((role) => {
                      const selected = roleIds.includes(role.id);
                      return (
                        <button
                          key={role.id}
                          type="button"
                          aria-pressed={selected}
                          onClick={() =>
                            setRoleIds((prev) =>
                              selected ? prev.filter((id) => id !== role.id) : [...prev, role.id],
                            )
                          }
                          className={cn(
                            'cursor-pointer rounded-full border px-3 py-1 text-sm transition-colors',
                            selected
                              ? 'bg-primary text-primary-foreground border-primary'
                              : 'border-input hover:bg-accent',
                          )}
                        >
                          {role.title}
                        </button>
                      );
                    })
                )}
              </div>
              <p className="text-muted-foreground text-[0.8rem]">
                A bot with no roles can authenticate but won&apos;t pass most permission checks.
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              <Label>Acts as</Label>
              <MemberSelect
                value={userId}
                onChange={setUserId}
                queryKey="pat-user-picker"
                placeholder="Select a person"
              />
              <p className="text-muted-foreground text-[0.8rem]">
                The token inherits this person&apos;s roles and project access — it can&apos;t do
                more than they can.
              </p>
            </div>
          )}

          {error ? <p className="text-destructive text-sm">{error}</p> : null}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="button" onClick={submit} disabled={create.isPending}>
            {create.isPending ? 'Creating…' : 'Create'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function KindCell({ bot }: { bot: Bot }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      {bot.kind === 'user' ? (
        <UserRound className="text-muted-foreground size-4 shrink-0" />
      ) : (
        <BotIcon className="text-muted-foreground size-4 shrink-0" />
      )}
      <Badge variant="secondary">{bot.kind === 'user' ? 'Personal' : 'Bot'}</Badge>
    </span>
  );
}

function StatusDot({ revoked }: { revoked: boolean }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-sm">
      <span
        className={cn(
          'inline-block size-2 shrink-0 rounded-full',
          revoked ? 'bg-muted-foreground/40' : 'bg-emerald-500',
        )}
      />
      {revoked ? 'Revoked' : 'Active'}
    </span>
  );
}

function RolesCell({ bot }: { bot: Bot }) {
  if (!bot.roles.length) return <span className="text-muted-foreground text-xs">No roles</span>;
  return (
    <div className="flex flex-wrap gap-1.5">
      {bot.roles.map((r) => (
        <Badge key={r} variant="outline">
          {r}
        </Badge>
      ))}
    </div>
  );
}

/** Rotate + revoke controls, shared by the table and card layouts. */
function BotActions({
  bot,
  onRevealKey,
  size = 'sm',
}: {
  bot: Bot;
  onRevealKey: (key: string) => void;
  size?: 'sm' | 'icon';
}) {
  const rotate = useRotateBotKey();
  const revoke = useRevokeBot();
  const [confirmRevoke, setConfirmRevoke] = useState(false);

  async function handleRotate() {
    const updated = await rotate.mutateAsync(bot.prefix);
    onRevealKey(updated.api_key);
  }

  return (
    <div className="flex items-center justify-end gap-2">
      <Button
        type="button"
        variant="outline"
        size={size}
        onClick={handleRotate}
        disabled={rotate.isPending}
        aria-label="Rotate key"
        title="Rotate key"
      >
        <RotateCw className="size-4" />
        {size === 'sm' ? 'Rotate' : null}
      </Button>
      {!bot.revoked ? (
        <Button
          type="button"
          variant="ghost"
          size={size}
          className="text-destructive hover:text-destructive"
          onClick={() => setConfirmRevoke(true)}
          aria-label="Revoke key"
          title="Revoke key"
        >
          <Trash2 className="size-4" />
          {size === 'sm' ? 'Revoke' : null}
        </Button>
      ) : null}

      <AlertDialog open={confirmRevoke} onOpenChange={setConfirmRevoke}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Revoke “{bot.name}”?</AlertDialogTitle>
            <AlertDialogDescription>
              Its API key stops working immediately. Anything using it will start getting 401s.
              This can&apos;t be undone — issue a new key if you need access again.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => revoke.mutate(bot.prefix)}
              className="bg-destructive text-white hover:bg-destructive/90"
            >
              Revoke
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

type StatusFilter = 'all' | 'active' | 'revoked';
type KindFilter = 'all' | 'bot' | 'user';
/** Columns the list can sort by (client-side — the list is small). */
type KeyColumn = 'name' | 'kind' | 'last_used_at' | 'created';

const VIEW_MODES: ViewModeOption<KeysViewMode>[] = [
  { value: 'table', label: 'Table', icon: TableIcon },
  { value: 'cards', label: 'Cards', icon: LayoutGrid },
];

function sortKeys(rows: Bot[], ordering: string | undefined): Bot[] {
  if (!ordering) return rows;
  const desc = ordering.startsWith('-');
  const col = (desc ? ordering.slice(1) : ordering) as KeyColumn;
  const val = (b: Bot): string => {
    if (col === 'name') return b.name.toLowerCase();
    if (col === 'kind') return b.kind;
    return b[col] ?? ''; // last_used_at / created — ISO strings sort lexically
  };
  const sorted = [...rows].sort((a, b) => val(a).localeCompare(val(b)));
  return desc ? sorted.reverse() : sorted;
}

/**
 * API keys & service bots (ADR-081). Founder / Super Admin only — gate at the
 * call site. A bot acts as a Member with the granted roles; a personal token
 * acts as an existing user. The raw key is shown exactly once (create + rotate).
 */
export function BotsCard() {
  const botsQuery = useBots();
  const { view, setView } = useKeysUi();
  const [createOpen, setCreateOpen] = useState(false);
  const [revealedKey, setRevealedKey] = useState<string | null>(null);

  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, 250);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [kindFilter, setKindFilter] = useState<KindFilter>('all');
  const [ordering, setOrdering] = useState<string | undefined>('-created');

  const rows = useMemo(() => {
    let out = botsQuery.data ?? [];
    if (statusFilter !== 'all') {
      out = out.filter((b) => (statusFilter === 'revoked' ? b.revoked : !b.revoked));
    }
    if (kindFilter !== 'all') out = out.filter((b) => b.kind === kindFilter);
    const q = debouncedSearch.trim().toLowerCase();
    if (q) {
      out = out.filter(
        (b) =>
          b.name.toLowerCase().includes(q) ||
          b.prefix.toLowerCase().includes(q) ||
          (b.bot_name ?? '').toLowerCase().includes(q),
      );
    }
    return sortKeys(out, ordering);
  }, [botsQuery.data, statusFilter, kindFilter, debouncedSearch, ordering]);

  const total = botsQuery.data?.length ?? 0;

  return (
    <Card>
      <CardContent className="space-y-4 pt-6">
        {/* Toolbar: search + filters + view toggle + create */}
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative min-w-[12rem] flex-1">
            <Search className="text-muted-foreground absolute top-1/2 left-2 size-4 -translate-y-1/2" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, prefix, person…"
              className="pl-8"
              aria-label="Search keys"
            />
          </div>

          <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as StatusFilter)}>
            <SelectTrigger className="w-32" aria-label="Status filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All status</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="revoked">Revoked</SelectItem>
            </SelectContent>
          </Select>

          <Select value={kindFilter} onValueChange={(v) => setKindFilter(v as KindFilter)}>
            <SelectTrigger className="w-32" aria-label="Type filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All types</SelectItem>
              <SelectItem value="bot">Bots</SelectItem>
              <SelectItem value="user">Personal</SelectItem>
            </SelectContent>
          </Select>

          <ViewModeToggle value={view} onChange={setView} modes={VIEW_MODES} />

          <Button type="button" size="sm" onClick={() => setCreateOpen(true)}>
            <Plus className="size-4" /> New bot
          </Button>
        </div>

        {/* Body */}
        {botsQuery.isPending ? (
          <Skeleton className="h-40 w-full" />
        ) : botsQuery.isError ? (
          <p className="text-destructive text-sm">
            {(botsQuery.error as unknown as ApiError)?.message ?? 'Failed to load keys.'}
          </p>
        ) : total === 0 ? (
          <p className="text-muted-foreground py-8 text-center text-sm">
            No bots yet. Create one to mint an API key.
          </p>
        ) : rows.length === 0 ? (
          <p className="text-muted-foreground py-8 text-center text-sm">
            No keys match your filters.
          </p>
        ) : view === 'table' ? (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <SortableTableHead column="name" value={ordering} onChange={setOrdering}>
                    Name
                  </SortableTableHead>
                  <SortableTableHead column="kind" value={ordering} onChange={setOrdering}>
                    Type
                  </SortableTableHead>
                  <TableHead>Prefix</TableHead>
                  <TableHead>Roles</TableHead>
                  <TableHead>Status</TableHead>
                  <SortableTableHead column="last_used_at" value={ordering} onChange={setOrdering}>
                    Last used
                  </SortableTableHead>
                  <SortableTableHead column="created" value={ordering} onChange={setOrdering}>
                    Created
                  </SortableTableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((bot) => (
                  <TableRow key={bot.prefix}>
                    <TableCell>
                      <span className="font-medium">{bot.name}</span>
                      {bot.kind === 'user' && bot.bot_name ? (
                        <span className="text-muted-foreground block text-xs">
                          Acts as {bot.bot_name}
                        </span>
                      ) : null}
                    </TableCell>
                    <TableCell>
                      <KindCell bot={bot} />
                    </TableCell>
                    <TableCell>
                      <code className="text-muted-foreground text-xs">{bot.prefix}</code>
                    </TableCell>
                    <TableCell>
                      <RolesCell bot={bot} />
                    </TableCell>
                    <TableCell>
                      <StatusDot revoked={bot.revoked} />
                    </TableCell>
                    <TableCell className="text-muted-foreground text-sm">
                      {formatDateTime(bot.last_used_at)}
                    </TableCell>
                    <TableCell className="text-muted-foreground text-sm">
                      {new Date(bot.created).toLocaleDateString()}
                    </TableCell>
                    <TableCell className="text-right">
                      <BotActions bot={bot} onRevealKey={setRevealedKey} size="icon" />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {rows.map((bot) => (
              <div key={bot.prefix} className="space-y-3 rounded-lg border p-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 space-y-1">
                    <div className="flex items-center gap-2">
                      <span className="truncate font-medium">{bot.name}</span>
                      <KindCell bot={bot} />
                    </div>
                    {bot.kind === 'user' && bot.bot_name ? (
                      <p className="text-muted-foreground text-xs">Acts as {bot.bot_name}</p>
                    ) : null}
                    <code className="text-muted-foreground text-xs">{bot.prefix}</code>
                  </div>
                  <StatusDot revoked={bot.revoked} />
                </div>
                <RolesCell bot={bot} />
                <p className="text-muted-foreground text-xs">
                  Last used {formatDateTime(bot.last_used_at)} · Created{' '}
                  {new Date(bot.created).toLocaleDateString()}
                </p>
                <BotActions bot={bot} onRevealKey={setRevealedKey} />
              </div>
            ))}
          </div>
        )}
      </CardContent>

      <CreateBotDialog open={createOpen} onOpenChange={setCreateOpen} onCreated={setRevealedKey} />
      <RevealKeyDialog apiKey={revealedKey} onClose={() => setRevealedKey(null)} />
    </Card>
  );
}
